import { z } from 'zod';
import { normalizeDrawingLayers } from '../shared/drawing-layer.js';
import { dataLayerPayloadSchema, randomDataId, calculateDataLayerBounds, type DataLayer } from '../shared/data-layer.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isSchemaRecord } from '../shared/runtime-values.js';
import type { DataLayerImportReport } from './data-layer-import.js';
import { mapFileStateSchema } from '../shared/map-file-state.js';
import { datasetBatches, datasetWriter } from './local-dataset-store.js';

export function readLayerFileStructure(value: SchemaInput, report: DataLayerImportReport): void {
  if (!isSchemaRecord(value)) return;
  if (value.drawonamapMap !== undefined) report.map = mapFileStateSchema.parse(value.drawonamapMap);
  if (Array.isArray(value.drawonamapDrawingLayers)) report.drawingLayers = normalizeDrawingLayers(value.drawonamapDrawingLayers);
  if (!value.drawonamapLayers) return;
  const definitions = z.array(dataLayerPayloadSchema.pick({ layer: true }).transform(payload => payload.layer)).max(100).parse(Array.isArray(value.drawonamapLayers) ? value.drawonamapLayers.map(layer => ({ layer })) : value.drawonamapLayers);
  if (new Set(definitions.map(layer => layer.id)).size !== definitions.length) throw new Error('Duplicate layer identifiers in file.');
  report.layerDefinitions = definitions.map(layer => ({ ...layer, saved: false, features: [] }));
}

function restoreSources(source: DataLayer, restored: DataLayer[]): void {
  if (source.sourceFiles?.some(file => file.kml)) {
    for (const layer of restored) layer.sourceFiles = layer.sourceFiles?.filter(file => !file.kml);
    if (restored[0]) restored[0].sourceFiles = [...restored[0].sourceFiles ?? [], ...source.sourceFiles.filter(file => file.kml)];
  }
  if (restored[0]) { restored[0].sourceFiles ??= source.sourceFiles; restored[0].retainedSources = source.retainedSources; }
}

/** Restore file structure with fresh IDs so importing twice cannot replace existing data. */
export async function restoreLayerFileStructure(report: DataLayerImportReport): Promise<DataLayer[]> {
  const source = report.layer;
  if (!source || !report.layerDefinitions?.length) return source ? [source] : [];
  const definitions = [...report.layerDefinitions].sort((left, right) => left.position - right.position);
  const groups = new Map(definitions.map(layer => [layer.id, { ...layer, id: randomDataId('layer'), features: [], featureCount: 0, saved: false } satisfies DataLayer]));
  const remainder: DataLayer = { ...source, id: randomDataId('layer'), features: [], featureCount: 0, dataset: undefined, sourceFiles: undefined, retainedSources: undefined };
  const writers = new Map<string, Awaited<ReturnType<typeof datasetWriter>>>();
  try {
    const batches = source.dataset ? datasetBatches(source.dataset) : [source.features];
    for await (const batch of batches) for (const feature of batch) {
      const layer: DataLayer = (feature.importLayerId ? groups.get(feature.importLayerId) : undefined) ?? remainder;
      const { importLayerId: _importLayerId, ...record } = feature;
      if (source.dataset) {
        let writer = writers.get(layer.id);
        if (!writer) { writer = await datasetWriter(); writers.set(layer.id, writer); }
        await writer.append(record);
      } else layer.features.push(record);
      layer.featureCount++;
    }
    const restored: DataLayer[] = [...groups.values(), ...(remainder.featureCount ? [remainder] : [])];
    for (let index = 0; index < restored.length; index++) {
      const layer = restored[index]; const writer = writers.get(layer.id);
      if (writer) restored[index] = await writer.finish(layer);
      else layer.bounds = calculateDataLayerBounds(layer.features);
    }
    restoreSources(source, restored);
    return restored;
  } finally { for (const writer of writers.values()) await writer.close(); }
}
